
import { CellError, ErrorType } from '../../Cell';
import { ErrorMessage } from '../../error-message';
import { coerceScalarToString } from '../ArithmeticHelper';
import { SimpleRangeValue } from '../../SimpleRangeValue';
import { isExtendedNumber } from '../InterpreterValue';
import { FunctionArgumentType, FunctionPlugin } from './FunctionPlugin';
/**
 * Interpreter plugin containing text-specific functions
 */
export class TextPlugin extends FunctionPlugin {
    /**
     * Corresponds to CONCATENATE(value1, [value2, ...])
     *
     * Concatenates provided arguments to one string.
     *
     * @param {ProcedureAst} ast - The procedure AST node
     * @param {InterpreterState} state - The interpreter state
     */
    concatenate(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('CONCATENATE'), (...args) => {
            return ''.concat(...args);
        });
    }
    /**
     * Corresponds to SPLIT(string, index)
     *
     * Splits provided string using space separator and returns chunk at zero-based position specified by second argument
     *
     * @param {ProcedureAst} ast - The procedure AST node
     * @param {InterpreterState} state - The interpreter state
     */
    split(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('SPLIT'), (stringToSplit, indexToUse) => {
            const splittedString = stringToSplit.split(' ');
            if (indexToUse >= splittedString.length || indexToUse < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.IndexBounds);
            }
            return splittedString[indexToUse];
        });
    }
    len(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('LEN'), (arg) => {
            return arg.length;
        });
    }
    lower(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('LOWER'), (arg) => {
            return arg.toLowerCase();
        });
    }
    trim(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('TRIM'), (arg) => {
            return arg
                .replace(/^ +/g, '')
                .replace(/ +$/g, '')
                .replace(/ +/g, ' ');
        });
    }
    proper(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('PROPER'), (arg) => {
            return arg.replace(/\p{L}+/gu, word => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase());
        });
    }
    clean(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('CLEAN'), (arg) => {
            // eslint-disable-next-line no-control-regex
            return arg.replace(/[\u0000-\u001F]/g, '');
        });
    }
    exact(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('EXACT'), (left, right) => {
            return left === right;
        });
    }
    rept(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('REPT'), (text, count) => {
            if (count < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.NegativeCount);
            }
            return text.repeat(count);
        });
    }
    right(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('RIGHT'), (text, length) => {
            if (length < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
            }
            else if (length === 0) {
                return '';
            }
            return text.slice(-length);
        });
    }
    left(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('LEFT'), (text, length) => {
            if (length < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
            }
            return text.slice(0, length);
        });
    }
    mid(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('MID'), (text, startPosition, numberOfChars) => {
            if (startPosition < 1) {
                return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
            }
            if (numberOfChars < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
            }
            return text.substring(startPosition - 1, startPosition + numberOfChars - 1);
        });
    }
    replace(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('REPLACE'), (text, startPosition, numberOfChars, newText) => {
            if (startPosition < 1) {
                return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
            }
            if (numberOfChars < 0) {
                return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
            }
            return text.substring(0, startPosition - 1) + newText + text.substring(startPosition + numberOfChars - 1);
        });
    }
    search(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('SEARCH'), (pattern, text, startIndex) => {
            if (startIndex < 1 || startIndex > text.length) {
                return new CellError(ErrorType.VALUE, ErrorMessage.LengthBounds);
            }
            const normalizedPattern = pattern.toLowerCase();
            const normalizedText = text.substring(startIndex - 1).toLowerCase();
            const index = this.arithmeticHelper.requiresRegex(normalizedPattern)
                ? this.arithmeticHelper.searchString(normalizedPattern, normalizedText)
                : normalizedText.indexOf(normalizedPattern);
            return index > -1 ? index + startIndex : new CellError(ErrorType.VALUE, ErrorMessage.PatternNotFound);
        });
    }
    substitute(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('SUBSTITUTE'), (text, searchString, replacementString, occurrenceNum) => {
            const escapedSearchString = this.escapeRegExpSpecialCharacters(searchString);
            const searchRegExp = new RegExp(escapedSearchString, 'g');
            if (occurrenceNum === undefined) {
                return text.replace(searchRegExp, replacementString);
            }
            if (occurrenceNum < 1) {
                return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
            }
            let match;
            let i = 0;
            while ((match = searchRegExp.exec(text)) !== null) {
                if (occurrenceNum === ++i) {
                    return text.substring(0, match.index) + replacementString + text.substring(searchRegExp.lastIndex);
                }
            }
            return text;
        });
    }
    find(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('FIND'), (pattern, text, startIndex) => {
            if (startIndex < 1 || startIndex > text.length) {
                return new CellError(ErrorType.VALUE, ErrorMessage.IndexBounds);
            }
            const shiftedText = text.substring(startIndex - 1);
            const index = shiftedText.indexOf(pattern) + startIndex;
            return index > 0 ? index : new CellError(ErrorType.VALUE, ErrorMessage.PatternNotFound);
        });
    }
    t(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('T'), (arg) => {
            if (arg instanceof CellError) {
                return arg;
            }
            return typeof arg === 'string' ? arg : '';
        });
    }
    /**
     * Corresponds to N(value)
     *
     * Converts a value to a number according to Excel specification:
     * - Numbers return themselves
     * - Dates return their serial number (stored as numbers internally)
     * - TRUE returns 1, FALSE returns 0
     * - Error values propagate
     * - Anything else (text, empty) returns 0
     * - For ranges, uses the first cell value
     */
    n(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('N'), (arg) => {
            var _a;
            const value = arg instanceof SimpleRangeValue ? (_a = arg.data[0]) === null || _a === void 0 ? void 0 : _a[0] : arg;
            if (value instanceof CellError) {
                return value;
            }
            if (typeof value === 'number') {
                return value;
            }
            if (typeof value === 'boolean') {
                return value ? 1 : 0;
            }
            return 0;
        });
    }
    upper(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('UPPER'), (arg) => {
            return arg.toUpperCase();
        });
    }
    /**
     * Corresponds to VALUE(text)
     *
     * Converts a text string that represents a number to a number.
     *
     * @param {ProcedureAst} ast - The procedure AST node
     * @param {InterpreterState} state - The interpreter state
     */
    value(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('VALUE'), (arg) => {
            if (arg instanceof CellError) {
                return arg;
            }
            if (isExtendedNumber(arg)) {
                return arg;
            }
            if (typeof arg !== 'string') {
                return new CellError(ErrorType.VALUE, ErrorMessage.NumberCoercion);
            }
            const trimmedArg = arg.trim();
            const parenthesesMatch = /^\(([^()]+)\)$/.exec(trimmedArg);
            if (parenthesesMatch) {
                const innerValue = this.parseStringToNumber(parenthesesMatch[1]);
                if (innerValue !== undefined) {
                    return -innerValue;
                }
            }
            const parsedValue = this.parseStringToNumber(trimmedArg);
            if (parsedValue !== undefined) {
                return parsedValue;
            }
            return new CellError(ErrorType.VALUE, ErrorMessage.NumberCoercion);
        });
    }
    /**
     * Corresponds to TEXTJOIN(delimiter, ignore_empty, text1, [text2], …)
     *
     * Joins text from multiple strings/ranges with a configurable delimiter.
     * Supports array/range delimiters that cycle through gaps between text values.
     * When ignore_empty is TRUE, empty strings are skipped.
     * Returns #VALUE! if the result exceeds 32,767 characters (Excel cell content limit).
     *
     * @param {ProcedureAst} ast - The procedure AST node
     * @param {InterpreterState} state - The interpreter state
     */
    textjoin(ast, state) {
        return this.runFunction(ast.args, state, this.metadata('TEXTJOIN'), (delimiterArg, ignoreEmpty, ...textArgs) => {
            const delimiters = this.flattenArgToStrings(delimiterArg);
            if (delimiters instanceof CellError) {
                return delimiters;
            }
            const texts = [];
            for (const arg of textArgs) {
                const coerced = this.flattenArgToStrings(arg);
                if (coerced instanceof CellError) {
                    return coerced;
                }
                texts.push(...coerced);
            }
            const parts = ignoreEmpty ? texts.filter((t) => t !== '') : texts;
            if (parts.length === 0) {
                return '';
            }
            const result = parts.reduce((acc, part, i) => i === 0 ? part : acc + delimiters[(i - 1) % delimiters.length] + part, '');
            if (result.length > 32767) {
                return new CellError(ErrorType.VALUE, ErrorMessage.ResultTooLong);
            }
            return result;
        });
    }
    /**
     * Flattens a scalar or range argument into an array of coerced strings.
     * Returns a CellError immediately if any value in the argument is an error or cannot be coerced.
     *
     * @param {InternalScalarValue | SimpleRangeValue} arg - Scalar or range to flatten
     * @returns {string[] | CellError} - Array of string values, or the first error encountered
     */
    flattenArgToStrings(arg) {
        const values = arg instanceof SimpleRangeValue ? arg.valuesFromTopLeftCorner() : [arg];
        const result = [];
        for (const val of values) {
            if (val instanceof CellError) {
                return val;
            }
            const coerced = coerceScalarToString(val);
            if (coerced instanceof CellError) {
                return coerced;
            }
            result.push(coerced);
        }
        return result;
    }
    /**
     * Parses a string to a numeric value, handling whitespace trimming and empty string validation.
     *
     * @param {string} input - The string to parse
     * @returns {Maybe<ExtendedNumber>} The parsed number or undefined if parsing fails or input is empty
     */
    parseStringToNumber(input) {
        const trimmedInput = input.trim();
        if (trimmedInput === '') {
            return undefined;
        }
        return this.arithmeticHelper.coerceToMaybeNumber(trimmedInput);
    }
    escapeRegExpSpecialCharacters(text) {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
TextPlugin.implementedFunctions = {
    'CONCATENATE': {
        method: 'concatenate',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ],
        repeatLastArgs: 1,
        expandRanges: true,
    },
    'EXACT': {
        method: 'exact',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'SPLIT': {
        method: 'split',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER },
        ]
    },
    'LEN': {
        method: 'len',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'LOWER': {
        method: 'lower',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'MID': {
        method: 'mid',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER },
            { argumentType: FunctionArgumentType.NUMBER },
        ]
    },
    'TRIM': {
        method: 'trim',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'T': {
        method: 't',
        parameters: [
            { argumentType: FunctionArgumentType.SCALAR }
        ]
    },
    'N': {
        method: 'n',
        parameters: [
            { argumentType: FunctionArgumentType.ANY }
        ]
    },
    'PROPER': {
        method: 'proper',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'CLEAN': {
        method: 'clean',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'REPT': {
        method: 'rept',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER },
        ]
    },
    'RIGHT': {
        method: 'right',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER, defaultValue: 1 },
        ]
    },
    'LEFT': {
        method: 'left',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER, defaultValue: 1 },
        ]
    },
    'REPLACE': {
        method: 'replace',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER },
            { argumentType: FunctionArgumentType.NUMBER },
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'SEARCH': {
        method: 'search',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER, defaultValue: 1 },
        ]
    },
    'SUBSTITUTE': {
        method: 'substitute',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER, optionalArg: true }
        ]
    },
    'FIND': {
        method: 'find',
        parameters: [
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.STRING },
            { argumentType: FunctionArgumentType.NUMBER, defaultValue: 1 },
        ]
    },
    'UPPER': {
        method: 'upper',
        parameters: [
            { argumentType: FunctionArgumentType.STRING }
        ]
    },
    'VALUE': {
        method: 'value',
        parameters: [
            { argumentType: FunctionArgumentType.SCALAR }
        ]
    },
    'TEXTJOIN': {
        method: 'textjoin',
        repeatLastArgs: 1,
        parameters: [
            { argumentType: FunctionArgumentType.ANY },
            { argumentType: FunctionArgumentType.BOOLEAN },
            { argumentType: FunctionArgumentType.ANY },
        ],
    },
};
